/**
 * Campaigns API
 */
import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Campaign {
    id: string;
    teamId: string;
    name: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    isArchived?: boolean;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    /** False for national campaigns when user is KretsAdmin (cannot edit/delete). */
    canManage?: boolean;
}

export interface CampaignCreateRequest {
    teamId: string;
    name: string;
    description?: string;
    startDate?: string;
    endDate?: string;
}

export interface CampaignUpdateRequest {
    name?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
}

export interface FlyerType {
    id: string;
    name: string;
    summary?: string;
    imageUrl?: string;
}

export interface CampaignFlyer {
    id: string;
    campaignId: string;
    flyerTypeId?: string;
    flyerType?: FlyerType;
    // Campaign-specific flyer properties (used when flyerTypeId is null)
    name: string;
    summary?: string;
    imageUrl?: string;
    weightGrams?: number;
    isDefault: boolean;
    priority?: number;
    plannedQuantity?: number;
}

export interface FlyerTypeCreateRequest {
    // Either flyerTypeId or name must be provided
    flyerTypeId?: string;
    // Campaign-specific flyer properties (required when flyerTypeId is null)
    name?: string;
    summary?: string;
    imageUrl?: string;
    weightGrams?: number;
    isDefault?: boolean;
    priority?: number;
    plannedQuantity?: number;
}

export interface FlyerTypeUpdateRequest {
    // Campaign-specific flyer properties (can be updated for campaign-specific flyers)
    name?: string;
    summary?: string;
    imageUrl?: string;
    weightGrams?: number;
    isDefault?: boolean;
    priority?: number;
    plannedQuantity?: number;
}

const compareFlyerNames = (a: string, b: string) =>
    a.localeCompare(b, "sv-SE", { sensitivity: "base" });

const sortCampaignFlyersByName = (flyers: CampaignFlyer[]): CampaignFlyer[] =>
    [...flyers].sort((a, b) => {
        const nameA = a.flyerType?.name ?? a.name ?? "";
        const nameB = b.flyerType?.name ?? b.name ?? "";
        return compareFlyerNames(nameA, nameB);
    });

const sortFlyerTypesByName = (flyers: FlyerType[]): FlyerType[] =>
    [...flyers].sort((a, b) => compareFlyerNames(a.name, b.name));

/**
 * Fetches all campaigns
 */
export const fetchCampaigns = async (): Promise<Campaign[]> => {
    const response = await apiClient.get<Campaign[]>("/campaigns");
    return response.data;
};

/**
 * Creates a new campaign
 */
export const createCampaign = async (
    request: CampaignCreateRequest
): Promise<Campaign> => {
    const response = await apiClient.post<Campaign>("/campaigns", request);
    return response.data;
};

/**
 * Updates an existing campaign
 */
export const updateCampaign = async (
    id: string,
    request: CampaignUpdateRequest
): Promise<Campaign> => {
    const response = await apiClient.put<Campaign>(`/campaigns/${id}`, request);
    return response.data;
};

/**
 * Deletes a campaign (soft delete)
 */
export const deleteCampaign = async (id: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${id}`);
};

/**
 * Fetches flyer types for a campaign (returns CampaignFlyer objects)
 */
export const fetchFlyerTypes = async (campaignId: string): Promise<CampaignFlyer[]> => {
    const response = await apiClient.get<CampaignFlyer[]>(`/campaigns/${campaignId}/flyer-types`);
    return sortCampaignFlyersByName(response.data);
};

/**
 * Fetches all available flyer types (not archived)
 */
export const fetchAvailableFlyerTypes = async (): Promise<FlyerType[]> => {
    const response = await apiClient.get<FlyerType[]>("/campaigns/flyer-types/available");
    return sortFlyerTypesByName(response.data);
};

export const fetchAllFlyerTypes = async (): Promise<FlyerType[]> => {
    const response = await apiClient.get<FlyerType[]>("/flyertypes");
    return sortFlyerTypesByName(response.data);
};

/**
 * Creates a flyer type for a campaign
 */
export const createFlyerType = async (
    campaignId: string,
    request: FlyerTypeCreateRequest
): Promise<CampaignFlyer> => {
    const response = await apiClient.post<CampaignFlyer>(
        `/campaigns/${campaignId}/flyer-types`,
        request
    );
    return response.data;
};

/**
 * Updates a flyer type for a campaign
 */
export const updateFlyerType = async (
    campaignId: string,
    id: string,
    request: FlyerTypeUpdateRequest
): Promise<CampaignFlyer> => {
    const response = await apiClient.put<CampaignFlyer>(
        `/campaigns/${campaignId}/flyer-types/${id}`,
        request
    );
    return response.data;
};

/**
 * Deletes a flyer type from a campaign
 */
export const deleteFlyerType = async (
    campaignId: string,
    id: string
): Promise<void> => {
    await apiClient.delete(`/campaigns/${campaignId}/flyer-types/${id}`);
};

// React Query hooks

/**
 * Hook for fetching campaigns
 */
export const useCampaigns = () => {
    return useQuery({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook for creating a campaign
 */
export const useCreateCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createCampaign,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        },
    });
};

/**
 * Hook for updating a campaign
 */
export const useUpdateCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, ...request }: { id: string } & CampaignUpdateRequest) =>
            updateCampaign(id, request),
        onMutate: async ({ id, ...newData }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ["campaigns"] });

            // Snapshot previous value
            const previousCampaigns = queryClient.getQueryData<Campaign[]>(["campaigns"]);

            // Optimistically update
            if (previousCampaigns) {
                queryClient.setQueryData<Campaign[]>(["campaigns"], (old) =>
                    old?.map((campaign) =>
                        campaign.id === id ? { ...campaign, ...newData } : campaign
                    )
                );
            }

            return { previousCampaigns };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousCampaigns) {
                queryClient.setQueryData(["campaigns"], context.previousCampaigns);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        },
    });
};

/**
 * Hook for deleting a campaign
 */
export const useDeleteCampaign = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteCampaign,
        onMutate: async (id) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ["campaigns"] });

            // Snapshot previous value
            const previousCampaigns = queryClient.getQueryData<Campaign[]>(["campaigns"]);

            // Optimistically remove
            if (previousCampaigns) {
                queryClient.setQueryData<Campaign[]>(["campaigns"], (old) =>
                    old?.filter((campaign) => campaign.id !== id)
                );
            }

            return { previousCampaigns };
        },
        onError: (_err, _id, context) => {
            // Rollback on error
            if (context?.previousCampaigns) {
                queryClient.setQueryData(["campaigns"], context.previousCampaigns);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        },
    });
};

/**
 * Hook for fetching flyer types for a campaign
 */
export const useFlyerTypes = (campaignId: string | null) => {
    return useQuery({
        queryKey: ["campaigns", campaignId, "flyer-types"],
        queryFn: () => fetchFlyerTypes(campaignId!),
        enabled: !!campaignId,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Hook for fetching all available flyer types
 */
export const useAvailableFlyerTypes = () => {
    return useQuery({
        queryKey: ["flyer-types", "available"],
        queryFn: fetchAvailableFlyerTypes,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Hook for creating a flyer type
 */
export const useCreateFlyerType = (campaignId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: FlyerTypeCreateRequest) =>
            createFlyerType(campaignId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["campaigns", campaignId, "flyer-types"],
            });
        },
    });
};

/**
 * Hook for updating a flyer type
 */
export const useUpdateFlyerType = (campaignId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            id,
            ...request
        }: { id: string } & FlyerTypeUpdateRequest) =>
            updateFlyerType(campaignId, id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["campaigns", campaignId, "flyer-types"],
            });
        },
    });
};

/**
 * Hook for deleting a flyer type
 */
export const useDeleteFlyerType = (campaignId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteFlyerType(campaignId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["campaigns", campaignId, "flyer-types"],
            });
        },
    });
};